import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient, { platformTokenStore } from '../api/client';

// Личность в модуле бывает ровно двух видов: собственная сессия standalone
// (`dashboard`) и её отсутствие. Режима `embed` здесь нет и быть не может — в MF
// личность приходит от платформы через `HostContext` и в стор модуля не кладётся.
type AuthMode = 'dashboard' | 'anonymous';

interface AuthState {
  mode: AuthMode;
  userId: string | null;
  email: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: (identifier: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  applyTokens: (accessToken: string, refreshToken: string) => void;
  resetAuth: () => void;
}

// Имя раздела zustand/persist в localStorage. Вынесено в константу, потому что
// disposeModuleState() обязан знать, что именно стирать: ключ живёт дольше
// модуля, и после unmount в нём остаются токены предыдущего пользователя.
export const AUTH_STORAGE_KEY = 'idea-calc-auth';

const CLEARED = {
  mode: 'anonymous' as AuthMode,
  userId: null,
  email: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isAdmin: false,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...CLEARED,
      isLoading: true,

      login: async (identifier: string, password: string) => {
        try {
          if (identifier.includes('@')) {
            const { data } = await apiClient.post('/api/auth/login', {
              email: identifier,
              password,
            });
            set({ accessToken: data.access_token, refreshToken: data.refresh_token });
          } else {
            await apiClient.post('/api/admin/login', {
              username: identifier,
              password,
            });
            set({ accessToken: null, refreshToken: null });
          }
          await get().fetchMe();
          return get().isAuthenticated;
        } catch {
          return false;
        }
      },

      logout: async () => {
        const { refreshToken } = get();
        if (refreshToken) {
          try {
            await apiClient.post('/api/auth/logout', { refresh_token: refreshToken });
          } catch { /* idempotent — даже невалидный токен это success */ }
        } else if (get().mode === 'dashboard') {
          try {
            await apiClient.post('/api/admin/logout');
          } catch { /* локальное состояние всё равно очищаем */ }
        }
        // Платформенный токен стирается ВМЕСТЕ с модульной сессией: он тоже
        // кредент личности (бэкенд принимает `X-Platform-Token`, когда Bearer'а
        // нет), и переживший выход токен вернул бы пользователя обратно на
        // первом же /api/auth/me. «Вышел, а всё ещё внутри» — не выход.
        platformTokenStore.clearRefreshToken();
        set({ ...CLEARED, isLoading: false });
      },

      // Валидация сессии при старте: если access протух — client-интерсептор
      // сам обновит его по refresh-токену и повторит запрос. Запрос также
      // восстанавливает admin_users-сессию из HttpOnly admin_token cookie.
      fetchMe: async () => {
        try {
          const { data } = await apiClient.get('/api/auth/me');
          set({
            mode: 'dashboard',
            userId: String(data.user_id),
            email: data.email ?? null,
            isAdmin: !!data.is_admin,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch {
          set({ ...CLEARED, isLoading: false });
        }
      },

      applyTokens: (accessToken: string, refreshToken: string) => {
        set({ accessToken, refreshToken });
      },

      resetAuth: () => set({ ...CLEARED, isLoading: false }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      // Персистим только токены — профиль (isAdmin/email) перечитываем через
      // /api/auth/me на старте, чтобы не доверять устаревшему localStorage.
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    },
  ),
);

/**
 * Возврат стора в состояние «только что импортирован» + стирание персиста.
 *
 * Зовётся из disposeModuleState() при размонтировании ремоута. Стор создаётся
 * один раз на загрузку страницы платформы и переживает unmount вместе со всем,
 * что в нём лежит; без этого следующий mount под ДРУГИМ пользователем подставил
 * бы в Authorization токен предыдущего.
 *
 * isLoading: true — то же значение, с которым стор рождается: следующий mount
 * обязан увидеть «личность ещё не выяснена», а не «выяснена и пуста».
 */
export function disposeAuthStore(): void {
  useAuthStore.setState({ ...CLEARED, isLoading: true });
  useAuthStore.persist.clearStorage();
}
